import { ApolloClient, ApolloLink, InMemoryCache, NormalizedCacheObject } from '@apollo/client';
import axios, { Method } from 'axios';
import Config from 'static/config/coreConfig';

export const GraphQLResponseLogger = new ApolloLink((operation, forward) => {
    const start = new Date();
    console.log(
        '%c ' + operation.operationName + ' ',
        'background-color: grey; color: white',
        operation.variables
    );
    return forward(operation).map((result) => {
        const end = new Date();
        const time = (Math.round(((end.getTime() - start.getTime()) / 1000) * 10) / 10).toFixed(1);
        console.log(
            '    => ' + time + 's  %c ' + operation.operationName + ' ',
            'background-color: #21ba45; color: white;',
            result.data
        );
        return result;
    });
});

const paginationStoriesMergeFunction = (previous, current) => {
    return {
        pageInfo: current?.pageInfo,
        stories: [...(previous?.stories ?? []), ...(current?.stories ?? [])],
        __typename: current?.__typename
    };
};

const paginationUsersMergeFunction = (previous, current) => {
    return {
        pageInfo: current?.pageInfo,
        users: [...(previous?.users ?? []), ...(current?.users ?? [])],
        __typename: current?.__typename
    };
};

export const createApolloClient = (link: ApolloLink): ApolloClient<NormalizedCacheObject> => {
    return new ApolloClient<NormalizedCacheObject>({
        defaultOptions: {
            watchQuery: {
                refetchWritePolicy: 'overwrite'
            }
        },
        cache: new InMemoryCache({
            typePolicies: {
                Map: {
                    keyFields: ['mapId']
                },
                PublishedStory: {
                    keyFields: ['storyId']
                },
                Story: {
                    keyFields: ['storyId']
                },
                GuideExternalLink: {
                    keyFields: ['guideExternalLinkId']
                },
                GuideEntityType: {
                    keyFields: ['guideEntityTypeId']
                },
                GuideProgramItem: {
                    keyFields: ['guideProgramItemId']
                },
                GuideNewsfeedItem: {
                    keyFields: ['guideNewsfeedItemId']
                },
                GuideInformationItem: {
                    keyFields: ['guideInformationItemId']
                },
                EventUser: {
                    keyFields: ['userId']
                },
                Guide: {
                    keyFields: ['guideId']
                },
                User: {
                    keyFields: ['userId']
                },
                UserMiniInfo: {
                    keyFields: ['userId']
                },
                UserProfile: {
                    keyFields: ['userId']
                },
                Marker: {
                    keyFields: ['markerId']
                },
                ExternalPoint: {
                    keyFields: ['externalPointId']
                },
                StorySet: {
                    keyFields: ['storySetId']
                },
                Card: {
                    keyFields: ['cardId']
                },
                Comment: {
                    keyFields: ['commentId']
                },
                Tour: {
                    keyFields: ['tourId']
                },
                WorldMap: {
                    keyFields: ['worldMapId']
                },
                WorldMapMarker: {
                    keyFields: ['worldMapMarkerId']
                },
                WorldMapSet: {
                    keyFields: ['worldMapSetId']
                },
                CustomLocation: {
                    keyFields: ['x', 'y']
                },
                Query: {
                    fields: {
                        publishedStoriesForFollowedUsers: {
                            keyArgs: ['type'],
                            merge: paginationStoriesMergeFunction
                        },
                        publishedStoriesPopular: {
                            keyArgs: ['timespan', 'type'],
                            merge: paginationStoriesMergeFunction
                        },
                        publishedStories: {
                            keyArgs: ['type', 'mapId'],
                            merge: paginationStoriesMergeFunction
                        },
                        publishedStoriesForTag: {
                            keyArgs: ['tag'],
                            merge: paginationStoriesMergeFunction
                        },
                        publishedStoriesForTagPopular: {
                            keyArgs: ['timespan', 'tag'],
                            merge: paginationStoriesMergeFunction
                        },
                        publishedStoriesForWorldMap: {
                            keyArgs: ['worldMapId']
                        },
                        publishedStoriesForUser: {
                            keyArgs: ['userId', 'type', 'mapId'],
                            merge: paginationStoriesMergeFunction
                        },
                        stories: {
                            keyArgs: [],
                            merge: paginationStoriesMergeFunction
                        },
                        publishedStoriesInStorySet: {
                            keyArgs: ['storySetId'],
                            merge: paginationStoriesMergeFunction
                        },
                        usersPopular: {
                            keyArgs: ['timespan'],
                            merge: paginationUsersMergeFunction
                        },
                        users: {
                            keyArgs: [],
                            merge: paginationUsersMergeFunction
                        },
                        getFollowedUsersForUser: {
                            keyArgs: ['userId'],
                            merge: paginationUsersMergeFunction
                        },
                        getUsersFollowingUser: {
                            keyArgs: ['userId'],
                            merge: paginationUsersMergeFunction
                        },
                        publishedStoriesForMap: {
                            keyArgs: ['mapId']
                        }
                    }
                }
            }
        }),
        link
    });
};

export function apiEndpoint(
    method: Method,
    url: string,
    params: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    headers: Record<string, unknown>
): Promise<Record<string, unknown>> {
    const start = new Date();
    console.log(
        '%c ' + url + ' ',
        'background-color: grey; color: white',
        method,
        params,
        data,
        headers
    );
    return new Promise((resolve, reject) => {
        axios({
            method,
            url: Config.ApiBaseUrl + url,
            headers: headers,
            params: params,
            data: data
        })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .then((r: any) => {
                const end = new Date();
                const time = (
                    Math.round(((end.getTime() - start.getTime()) / 1000) * 10) / 10
                ).toFixed(1);
                console.log(
                    '    => ' + time + 's  %c ' + url + ' ',
                    'background-color: #21ba45; color: white;',
                    r.data
                );
                resolve(r);
            })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .catch((r: any) => {
                const end = new Date();
                const time = (
                    Math.round(((end.getTime() - start.getTime()) / 1000) * 10) / 10
                ).toFixed(1);
                console.error(
                    '    => ' + time + 's %c ' + url + ' ',
                    'background-color: red; color: white;',
                    r
                );
                reject(r);
                throw r;
            });
    });
}
